import { NATIVE_EVENTS } from '../constants/events';

type HostEvent = {
  type: (typeof NATIVE_EVENTS)[keyof typeof NATIVE_EVENTS];
  payload: Record<string, unknown>;
};

type GlobalWithRN = typeof globalThis & {
  ReactNativeWebView?: { postMessage: (message: string) => void };
};

type HostBridgeContinueOptions = {
  message: string;
  showToast?: (message: string) => void;
  exitDelayMs?: number;
  nativeEventType?: HostEvent['type'];
};

const EXIT_DELAY_MS = 5000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function postMessageToHost(event: HostEvent): void {
  const _global = globalThis as GlobalWithRN;
  const serialized = JSON.stringify(event);

  if (_global.ReactNativeWebView?.postMessage) {
    _global.ReactNativeWebView.postMessage(serialized);
    return;
  }

  if (globalThis.opener) {
    (globalThis.opener as Window).postMessage(event, globalThis.location.origin);
  }
}

function closeWindowSafely(): void {
  if (globalThis.document.visibilityState !== 'visible') return;
  try {
    globalThis.close();
  } catch {
    /* close not always permitted */
  }
}

/**
 * Sends a GAME_COMPLETED event to the host environment:
 *   1. React Native WebView  → postMessage (stringified JSON)
 *   2. Browser popup/new-tab → globalThis.opener.postMessage, then globalThis.close()
 *   3. Standalone fallback   → console.info (no-op for UX)
 */
export const HostBridge = {
  async continue(
    payload: Record<string, unknown>,
    options: HostBridgeContinueOptions,
  ): Promise<void> {
    if (!('window' in globalThis)) return;

    const event: HostEvent = {
      type: options.nativeEventType ?? NATIVE_EVENTS.GAME_COMPLETED,
      payload,
    };
    postMessageToHost(event);
    options.showToast?.(options.message);
    await sleep(options.exitDelayMs ?? EXIT_DELAY_MS);
    closeWindowSafely();

    // NOTE : For debugging
    // console.info("[HostBridge] GAME_COMPLETED", payload);
  },
} as const;
